import { useCallback, useEffect, useRef, useState } from 'react';
import { useSelectedAccount } from '@ibk/shared-auth';
import { PaymentRequestApprovalStatus } from '@ibk/shared-types';
import { localStorageService, StorageKeys } from '@ibk/shared-utils';
import { useLazyGetPaymentsV2Query } from '../../services/paymentsApi';
import { RequestTimeoutState, useRequestTimeout } from '../useRequestTimeout';
const PAGE_SIZE = 10;
export function useGetInstructions(status, approvalStatus, search) {
    const { selectedAccount } = useSelectedAccount();
    const [getPaymentsV2] = useLazyGetPaymentsV2Query();
    const [instructionsData, setInstructionsData] = useState([]);
    const [searchedData, setSearchedData] = useState([]);
    const [hasMoreData, setHasMoreData] = useState(true);
    const [loading, setLoading] = useState(false);
    const [page, setPage] = useState(1);
    const { state: requestState, startTracking, stopTracking, reset: resetRequestState } = useRequestTimeout();
    const requestIdRef = useRef(0);
    const timedOutRef = useRef(false);
    const innerSearch = search.length >= 3 ? search : '';
    useEffect(() => {
        if (requestState === RequestTimeoutState.Timeout) {
            timedOutRef.current = true;
            setLoading(false);
        }
    }, [requestState]);
    const shouldSkipRequest = useCallback((forceNewRequest) => {
        return !selectedAccount?.targetId || ((loading || !hasMoreData) && !forceNewRequest);
    }, [selectedAccount, loading, hasMoreData]);
    const loadStoredPendingPayments = useCallback(async () => {
        if (approvalStatus !== PaymentRequestApprovalStatus.PENDING || instructionsData.length) {
            return false;
        }
        const storedTotal = await localStorageService.getItem(StorageKeys.PendingPaymentsTotal);
        if (!storedTotal) {
            return false;
        }
        const storedList = await localStorageService.getItem(StorageKeys.PendingPaymentsList);
        if (storedList && storedList.length > 0) {
            setInstructionsData(storedList);
            window.dispatchEvent(new CustomEvent('localStorageChange', { detail: { key: StorageKeys.PendingPaymentsTotal } }));
        }
        return true;
    }, [approvalStatus, instructionsData.length]);
    const storePendingPaymentsTotal = useCallback(async (total, page, globalSearch) => {
        const shouldStore = approvalStatus === PaymentRequestApprovalStatus.PENDING && !globalSearch?.length && page === 1;
        if (shouldStore) {
            await localStorageService.setItem(StorageKeys.PendingPaymentsTotal, total);
            window.dispatchEvent(new CustomEvent('localStorageChange', { detail: { key: StorageKeys.PendingPaymentsTotal } }));
        }
    }, [approvalStatus]);
    const shouldStorePendingList = useCallback((page) => page === 1 && approvalStatus === PaymentRequestApprovalStatus.PENDING, [approvalStatus]);
    const updateDataWithResults = useCallback(async (resultData, page, globalSearch) => {
        setHasMoreData(PAGE_SIZE <= resultData.length);
        setPage(page + 1);
        const isFirstPage = page === 1;
        const dataOrUpdateFn = isFirstPage ? resultData : (prev) => [...prev, ...resultData];
        if (globalSearch) {
            setSearchedData(dataOrUpdateFn);
            return;
        }
        setInstructionsData(dataOrUpdateFn);
        if (shouldStorePendingList(page)) {
            await localStorageService.setItem(StorageKeys.PendingPaymentsList, resultData);
        }
    }, [approvalStatus, shouldStorePendingList]);
    const fetchPayments = useCallback(async (page, globalSearch, forceNewRequest) => {
        if (shouldSkipRequest(forceNewRequest)) {
            return;
        }
        setLoading(true);
        if (!forceNewRequest) {
            const hasStoredData = await loadStoredPendingPayments();
            if (hasStoredData) {
                resetRequestState();
                setLoading(false);
                return;
            }
        }
        const requestId = ++requestIdRef.current;
        timedOutRef.current = false;
        startTracking();
        try {
            const { result } = await getPaymentsV2({
                approvalState: approvalStatus,
                status,
                page,
                pageSize: PAGE_SIZE,
                partyId: selectedAccount.targetId,
                globalSearch,
            }).unwrap();
            if (requestId !== requestIdRef.current || timedOutRef.current) {
                return;
            }
            await storePendingPaymentsTotal(result.pagination.total, page, globalSearch);
            if (result) {
                updateDataWithResults(result.data, page, globalSearch);
            }
            stopTracking(RequestTimeoutState.Success);
        }
        catch (error) {
            if (requestId !== requestIdRef.current || timedOutRef.current) {
                return;
            }
            setHasMoreData(false);
            stopTracking(RequestTimeoutState.Error);
        }
        finally {
            if (requestId === requestIdRef.current && !timedOutRef.current) {
                setLoading(false);
            }
        }
    }, [
        selectedAccount,
        approvalStatus,
        status,
        getPaymentsV2,
        shouldSkipRequest,
        loadStoredPendingPayments,
        storePendingPaymentsTotal,
        updateDataWithResults,
        resetRequestState,
        startTracking,
        stopTracking,
    ]);
    const fetchPaymentsRef = useRef(fetchPayments);
    fetchPaymentsRef.current = fetchPayments;
    useEffect(() => {
        if (innerSearch.length < 3) {
            return;
        }
        const handler = setTimeout(() => {
            fetchPaymentsRef.current(1, innerSearch, true);
        }, 500);
        return () => {
            clearTimeout(handler);
        };
    }, [innerSearch, instructionsData.length]);
    useEffect(() => {
        if (innerSearch.length === 0 && searchedData.length > 0) {
            setSearchedData([]);
            setPage(1);
            setHasMoreData(true);
            fetchPaymentsRef.current(1, undefined, true);
        }
    }, [innerSearch, searchedData.length]);
    useEffect(() => {
        setPage(1);
        setLoading(false);
        setHasMoreData(true);
        setInstructionsData([]);
        setSearchedData([]);
        fetchPaymentsRef.current(1);
    }, [selectedAccount?.targetId]);
    useEffect(() => {
        const handlePaymentUpdate = () => {
            setInstructionsData([]);
            setSearchedData([]);
            setPage(1);
            setHasMoreData(true);
            fetchPaymentsRef.current(1, innerSearch, true);
        };
        window.addEventListener('payment-approved', handlePaymentUpdate);
        window.addEventListener('payment-reproved', handlePaymentUpdate);
        return () => {
            window.removeEventListener('payment-approved', handlePaymentUpdate);
            window.removeEventListener('payment-reproved', handlePaymentUpdate);
        };
    }, [innerSearch]);
    const getNextPage = () => {
        if (!hasMoreData)
            return;
        fetchPayments(page, innerSearch);
    };
    const dataBeingValidated = innerSearch.length ? searchedData : instructionsData;
    const retryFetch = useCallback(() => {
        resetRequestState();
        setInstructionsData([]);
        setSearchedData([]);
        setPage(1);
        setHasMoreData(true);
        fetchPayments(1, innerSearch, true);
    }, [fetchPayments, innerSearch, resetRequestState]);
    return {
        instructionsData: dataBeingValidated,
        hasNoData: !loading && !dataBeingValidated.length,
        initialLoading: loading && !dataBeingValidated.length,
        loadingMore: loading && !!dataBeingValidated.length,
        hasMoreData,
        getNextPage,
        requestState,
        retryFetch,
    };
}
